// ============================================================
// documentReader.js — AI document reading for PDF uploads
//
// Primary reader: LlamaParse (parse-document start/status, async job).
// Backup reader: Claude vision (parse-document vision_* actions), used
// automatically when LlamaParse can't read the file — e.g. it ran out of
// credits on 2026-09-26 and the upload silently fell back to pdf-parse's
// garbage text layer, which then failed Level 2 with a misleading "bad scan"
// message.
//
// Never falls back silently: the result always says which reader produced the
// text, and a failure carries a plain-English reason for the admin.
// ============================================================

import { supabase } from '../lib/supabase';

const POLL_MS = 4000;
const PRIMARY_TIMEOUT_MS = 8 * 60 * 1000;
const VISION_PAGES_PER_CALL = 4;
const VISION_PARALLEL_CALLS = 2;

const REASON_MESSAGES = {
  out_of_credits: 'The AI document reader is out of credits.',
  reader_not_configured: "The AI document reader isn't set up on the server.",
  reader_auth: 'The AI document reader rejected the server\'s credentials.',
  rate_limited: 'The AI document reader is busy right now.',
  reader_unavailable: 'The AI document reader is temporarily unavailable.',
  reader_failed: 'The AI document reader could not read this file.',
  timeout: 'The AI document reader took too long to read this file.',
  network: 'The AI document reader could not be reached.',
};

export function describeReaderFailure(reason) {
  return REASON_MESSAGES[reason] || REASON_MESSAGES.reader_failed;
}

// supabase.functions.invoke returns { data, error }; handled reader failures
// arrive as HTTP 200 with { status: 'error', reason }.
async function callParseDocument(invoke, body) {
  const { data, error } = await invoke('parse-document', { body });
  if (error) return { status: 'error', reason: data?.reason || 'network', error: data?.error || error.message };
  return data || { status: 'error', reason: 'reader_failed', error: 'empty response' };
}

async function readWithLlamaParse(invoke, ids, report, sleep) {
  report('Reading document with AI…');
  const started = await callParseDocument(invoke, { action: 'start', ...ids });
  if (started.status === 'error' || !started.job_id) {
    return { ok: false, reason: started.reason || 'reader_failed', error: started.error };
  }
  const t0 = Date.now();
  while (Date.now() - t0 < PRIMARY_TIMEOUT_MS) {
    await sleep(POLL_MS);
    report(`Reading document with AI… (${Math.round((Date.now() - t0) / 1000)}s)`);
    const res = await callParseDocument(invoke, { action: 'status', ...ids, job_id: started.job_id });
    if (res.status === 'pending') continue;
    if (res.status === 'success' && res.text) return { ok: true, result: toExtraction(res, 'llamaparse_agentic') };
    return { ok: false, reason: res.reason || 'reader_failed', error: res.error };
  }
  return { ok: false, reason: 'timeout' };
}

async function readWithClaudeVision(invoke, ids, report) {
  const info = await callParseDocument(invoke, { action: 'vision_info', ...ids });
  if (info.status !== 'success' || !info.pageCount) return { ok: false, reason: info.reason || 'reader_failed', error: info.error };

  const ranges = [];
  for (let from = 0; from < info.pageCount; from += VISION_PAGES_PER_CALL) {
    ranges.push([from, Math.min(from + VISION_PAGES_PER_CALL, info.pageCount) - 1]);
  }
  const pages = new Array(info.pageCount);
  let done = 0;
  let failure = null;
  const worker = async () => {
    while (ranges.length && !failure) {
      const [from, to] = ranges.shift();
      const res = await callParseDocument(invoke, { action: 'vision_pages', ...ids, from, to });
      if (res.status !== 'success') { failure = res; return; }
      for (const p of res.pages || []) pages[p.index] = p.markdown;
      done += (res.pages || []).length;
      report(`Reading document with the backup reader… page ${done} of ${info.pageCount}`);
    }
  };
  await Promise.all(Array.from({ length: VISION_PARALLEL_CALLS }, worker));
  if (failure) return { ok: false, reason: failure.reason || 'reader_failed', error: failure.error };
  if (pages.some((p) => typeof p !== 'string')) return { ok: false, reason: 'reader_failed', error: 'missing pages' };

  const text = pages.map((p) => p.trim()).join('\n\n---\n\n');
  const saved = await callParseDocument(invoke, { action: 'vision_finalize', ...ids, text, pageCount: info.pageCount });
  if (saved.status !== 'success') return { ok: false, reason: saved.reason || 'reader_failed', error: saved.error };
  return { ok: true, result: toExtraction(saved, 'claude_vision') };
}

function toExtraction(res, method) {
  return {
    text: res.text,
    wordCount: res.wordCount || 0,
    pageCount: res.pageCount || 0,
    method: res.method || method,
    quality: res.quality || 'full',
  };
}

// Returns { ok: true, result, notice } or { ok: false, reason, message }.
// `notice` is set when the backup reader was used, so the admin knows why.
export async function readDocumentWithAI(documentId, hoaId, onStatus = null, deps = {}) {
  const invoke = deps.invoke || ((name, opts) => supabase.functions.invoke(name, opts));
  const sleep = deps.sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  const report = (message) => { if (onStatus) onStatus(message); };
  const ids = { document_id: documentId, hoa_id: hoaId };

  try {
    const primary = await readWithLlamaParse(invoke, ids, report, sleep);
    if (primary.ok) return primary;

    console.warn('[documentReader] Primary reader failed, switching to backup:', primary.reason, primary.error || '');
    report(`${describeReaderFailure(primary.reason)} Switching to the backup reader…`);
    const backup = await readWithClaudeVision(invoke, ids, report);
    if (backup.ok) {
      return { ...backup, notice: `${describeReaderFailure(primary.reason)} This document was read with the backup reader instead.` };
    }
    console.error('[documentReader] Backup reader failed too:', backup.reason, backup.error || '');
    return {
      ok: false,
      reason: primary.reason,
      message: `${describeReaderFailure(primary.reason)} The backup reader also failed (${describeReaderFailure(backup.reason).replace(/\.$/, '')}).`,
    };
  } catch (err) {
    console.error('[documentReader] Unexpected error:', err);
    return { ok: false, reason: 'reader_failed', message: describeReaderFailure('reader_failed') };
  }
}
